<script setup lang="ts">
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import { watch } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { onDeepLink, onResume } from '@/native/appEvents'
import { clearNativeContext, setNativeContext } from '@/native/captureInbox'
import { registerForPush } from '@/native/push'
import { connectRealtime, disconnectRealtime, ensureRealtime } from '@/realtime/socket'
import { useAuthStore } from '@/stores/auth'
import { useAiStore } from '@/stores/ai'
import { useCaptureQueueStore } from '@/stores/captureQueue'
import { useGarageStore } from '@/stores/garage'
import { useIntervalsStore } from '@/stores/intervals'
import { useNotificationsStore } from '@/stores/notifications'
import { useRidesStore } from '@/stores/rides'
import { useStravaStore } from '@/stores/strava'
import { usePhotosStore } from '@/stores/photos'

const auth = useAuthStore()
const captureQueue = useCaptureQueueStore()
const notifications = useNotificationsStore()
const toast = useToast()
const router = useRouter()
const garage = useGarageStore()

// Links from widgets, tiles and shortcuts: klunkers://capture opens the
// capture sheet; any other klunkers://<path> opens that page.
void onDeepLink((path) => {
  if (path === 'capture') captureQueue.requestSheet()
  else void router.push(`/${path}`)
})
// The same from a web or PWA shortcut: any page with ?capture=1.
watch(
  () => router.currentRoute.value.query.capture,
  (flag) => {
    if (flag !== '1') return
    captureQueue.requestSheet()
    const { capture: _drop, ...query } = router.currentRoute.value.query
    void router.replace({ query })
  },
)

// Native quick capture offers the rider's bikes without the app running.
watch(
  () => [auth.userId, garage.bikes, garage.defaultBike?.id] as const,
  ([id]) => {
    if (!id) return void clearNativeContext()
    void setNativeContext({
      user_id: id,
      bikes: garage.bikes.map((b) => ({ id: b.id, name: b.name })),
      current_bike_id: garage.defaultBike?.id ?? null,
    })
  },
  { immediate: true },
)

// Replay captures saved offline once we know who is signed in. That is known
// from the stored session, so this also runs when the app starts with no signal.
watch(
  () => auth.userId,
  (id) => {
    if (!id) return
    captureQueue.start()
    void captureQueue.refresh().then(() => captureQueue.syncNow())
    void notifications.load().catch(() => {})
    void useAiStore().loadStatus()
    // Push on phones (no-op on the web). Tapping a push opens its link.
    void registerForPush((link) => {
      if (link) void router.push(link)
    })
  },
  { immediate: true },
)

// Phones drop the connection in the background: pick it up again on return.
void onResume(ensureRealtime)

// Live updates while signed in.
watch(
  () => auth.isSignedIn,
  (signedIn) => {
    if (signedIn) {
      const socket = connectRealtime(
        () => auth.accessToken,
        () => auth.refresh(),
      )
      const photos = usePhotosStore()
      socket.on(
        'photo.processed',
        (payload: { owner_type: string; owner_id: string }) => void photos.processed(payload),
      )
      socket.on('ai.suggestion.ready', (payload: { suggestion_id: string }) => {
        void useAiStore()
          .refresh(payload.suggestion_id)
          .catch(() => {})
      })
      socket.on('rides.synced', () => {
        useRidesStore().stale()
        void useStravaStore()
          .load()
          .catch(() => {})
      })
      socket.on('notification.created', (payload: { title: string; body: string }) => {
        toast.add({ severity: 'warn', summary: payload.title, detail: payload.body, life: 8000 })
        void notifications.load().catch(() => {})
        void useIntervalsStore().refresh()
      })
    } else disconnectRealtime()
  },
  { immediate: true },
)
</script>

<template>
  <RouterView />
  <Toast position="top-center" />
</template>
